import { DEFAULT_MASK_OPACITY } from 'components/Canvas/Mask';
import { either } from 'fp-ts';
import { observer } from 'mobx-react-lite';
import { pipe } from 'fp-ts/lib/function';
import { useMstPredictionStore, useUnsafePredictionStore } from 'modules/Predictions/contexts/MstPredictionStoreCtx';
import { PredictionViewConfig } from 'modules/Predictions/types';
import { ComponentProps, useCallback } from 'react';

import { RegionFocusKind } from 'modules/Predictions/mst/basePredictionStore';
import { RegionMap, CanvasRegionList, MakeCanvasItemProps, NullableShapeConfig } from 'modules/Canvas/components/CanvasRegionList/CanvasRegionList';
import { throwIfNotExhaustive } from 'utils/ts';
import { IWorkflowPredictionStoreMST } from 'modules/Predictions/mst/WorkflowPredictionStore';
import { buildRegionMap } from 'utils/canvas';
import { LabelOrientationEnum } from 'components/Canvas/RegionLabel';
import isEmpty from 'lodash/isEmpty';
import { PredUiRegionItem } from 'utils/cfUiItems/predictions';

function RegionList_({
  scale = 1,
  media,
  predictionStore,
}: {
  scale?: number;
  media?: HTMLImageElement | HTMLVideoElement;
  predictionStore?: IWorkflowPredictionStoreMST;
}): JSX.Element | null {
  const store = predictionStore || useUnsafePredictionStore<CF.API.Workflows.Workflow, IWorkflowPredictionStoreMST>();
  if (!store) return null;
  const modelOutputSettings = store?.ModelOutputSettings;

  const focusKind = store?.viewState?.focusKind || 'none';
  const focusedRegion = store.viewState?.focusedRegion || '';

  const makeItemProps: MakeCanvasItemProps = (item) => {
    if (!item) return { opacity: 1 };

    const focusRegion = useCallback(
      (itemKey: string) => {
        if (store) {
          store.viewState.setFocusedRegion('canvas', itemKey);
        }
      },
      [store],
    );

    const unfocusRegion = useCallback(() => {
      if (store) {
        store.viewState.clearFocusedRegion();
      }
    }, [store]);

    const opacity = calculateRegionOpacity(focusedRegion, focusKind, item);

    return {
      onMouseEnter: () => focusRegion(item.key),
      onMouseLeave: () => unfocusRegion(),
      opacity,
      orientation: LabelOrientationEnum.BOTTOM,
    };
  };

  if (!media) return null;

  const regionMap: RegionMap = pipe(
    store.outputE,
    either.match(
      () => ({} as RegionMap),
      (output) =>
        buildRegionMap(
          output.map(({ data, model }) => {
            return {
              ...data,
              ...model,
            };
          }) as CF.API.TOutputData[],
          modelOutputSettings,
        ),
    ),
  );

  return pipe(
    store.regionListE,
    either.fold(
      () => null,
      (list) => {
        const filteredListObj = {
          items: list.items.filter(shouldFilterRegion(regionMap)),
        };
        return (
          <CanvasRegionList {...{ media, regionMap, list: filteredListObj, scale }} makeItemProps={makeItemProps} isBboxesVisible isPolygonsVisible />
        );
      },
    ),
  );
}

const PredictionRegionList__: PredictionViewConfig['canvas']['RegionList'] = observer(RegionList_);

const withGuaranteedPredStoreLocal = <P extends ComponentProps<typeof PredictionRegionList__>>(Component: React.ComponentType<P>): React.FC<P> => {
  const Enhanced: React.FC<P> = (props: P) => {
    if (props?.predictionStore) return <Component {...props} />;
    const store = useMstPredictionStore();
    return store ? <Component {...props} /> : null;
  };
  return Enhanced;
};

export const PredictionRegionList = withGuaranteedPredStoreLocal(PredictionRegionList__);
PredictionRegionList.displayName = 'PredictionRegionList';

const shouldFilterRegion =
  (regionMap: RegionMap) =>
  (region: PredUiRegionItem): boolean => {
    // Reason for this change is because as per issue EAGLE-3670, on OCR models regions were not being shown since
    // OCR models dont output a list of concepts. So we will show regions if either they have concepts or text.
    const regionHasConcepts = (reg: PredUiRegionItem) => !isEmpty(reg?.concepts);
    const regionHasText = (reg: PredUiRegionItem) => 'text' in reg;
    const isRegionInMap = ({ key }: PredUiRegionItem) => key in regionMap;

    return (regionHasConcepts(region) || regionHasText(region)) && isRegionInMap(region);
  };

function calculateRegionOpacity(focusedRegion: string, focusKind: RegionFocusKind, item: NullableShapeConfig): number {
  const focusOpacity = item && 'base64' in item ? DEFAULT_MASK_OPACITY : 1;
  const nonFocusOpacity = 0.25;
  switch (focusKind) {
    // if no focus, or focusing is happening on canvas return undefined so everything can use its default opacity
    case 'none':
    case 'canvas':
      // When you hover over a region, all other regions have reduced opacity. By default all regions are opaque.
      return focusedRegion ? (item?.key === focusedRegion ? focusOpacity : nonFocusOpacity) : focusOpacity;
    case 'sidebar-region':
      return item?.key === focusedRegion ? focusOpacity : nonFocusOpacity;
    case 'sidebar-concept':
      return item?.concept?.id === focusedRegion.split('##cf##')[0] ? focusOpacity : nonFocusOpacity;
    default:
      throwIfNotExhaustive(focusKind);
  }
  return 1;
}
